import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Overline } from "@/components/public/ui";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BESOINS = ["Logiciel de gestion", "Développement sur mesure", "Site / Application web", "Application mobile", "Matériel informatique", "Infrastructure & Réseau", "Cybersécurité", "Maintenance", "Autre"];
const BUDGETS = ["< 500 000 FCFA", "500 000 – 2M FCFA", "2M – 5M FCFA", "> 5M FCFA", "À définir"];
const DELAIS = ["Urgent (< 1 mois)", "1 – 3 mois", "3 – 6 mois", "Flexible"];

export default function Devis() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ nom: "", entreprise: "", email: "", telephone: "", whatsapp: "", type_besoin: "", solution_service: "", budget: "", delai: "", description: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const submit = async () => {
    if (!form.nom || !form.email) { toast.error("Nom et email requis."); setStep(3); return; }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-6 py-28 text-center">
      <div className="w-16 h-16 rounded-full bg-[#4CB944]/15 grid place-items-center mx-auto mb-6"><Check className="w-8 h-8 text-[#4CB944]" /></div>
      <h1 className="text-3xl font-bold text-slate-900 font-head">Demande envoyée !</h1>
      <p className="mt-3 text-slate-600">Merci {form.nom}. Un email de confirmation vous a été envoyé. Notre équipe commerciale vous recontactera très rapidement.</p>
      <a href="/" className="inline-block mt-8 px-6 py-3 rounded-full bg-[#0F2A6B] text-white font-semibold">Retour à l'accueil</a>
    </div>
  );

  return (
    <div>
      <section className="bg-gradient-to-br from-[#0F2A6B] to-[#0A1F52] relative grain">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 relative">
          <Overline><span className="text-[#4CB944]">Demande de devis</span></Overline>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-white font-head">Obtenez votre devis gratuit</h1>
          <div className="mt-8 flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/15"><div className={`h-full bg-[#4CB944] transition-all ${step >= s ? "w-full" : "w-0"}`} /></div>
            ))}
          </div>
          <div className="mt-2 text-sm text-slate-300 font-mono">Étape {step} / 3</div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm">
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900 font-head">Votre besoin</h2>
              <div>
                <label className="text-sm font-medium text-slate-700">Type de besoin</label>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {BESOINS.map((b) => (
                    <button key={b} data-testid={`devis-besoin-${b}`} onClick={() => set("type_besoin", b)} className={`text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all ${form.type_besoin === b ? "border-[#0F2A6B] bg-[#0F2A6B]/5 text-[#0F2A6B]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{b}</button>
                  ))}
                </div>
              </div>
              <div><label className="text-sm font-medium text-slate-700">Solution / Service concerné</label><Input value={form.solution_service} onChange={(e) => set("solution_service", e.target.value)} className="mt-1.5" placeholder="Ex : Vision POS, câblage réseau…" /></div>
              <div className="flex justify-end"><button data-testid="devis-next-1" onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0F2A6B] text-white font-semibold">Suivant <ArrowRight className="w-4 h-4" /></button></div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900 font-head">Budget & délai</h2>
              <div><label className="text-sm font-medium text-slate-700">Budget estimé</label><div className="mt-3 flex flex-wrap gap-2">{BUDGETS.map((b) => (<button key={b} onClick={() => set("budget", b)} className={`px-4 py-2 rounded-full border text-sm ${form.budget === b ? "border-[#0F2A6B] bg-[#0F2A6B]/5 text-[#0F2A6B]" : "border-slate-200 text-slate-600"}`}>{b}</button>))}</div></div>
              <div><label className="text-sm font-medium text-slate-700">Délai souhaité</label><div className="mt-3 flex flex-wrap gap-2">{DELAIS.map((d) => (<button key={d} onClick={() => set("delai", d)} className={`px-4 py-2 rounded-full border text-sm ${form.delai === d ? "border-[#0F2A6B] bg-[#0F2A6B]/5 text-[#0F2A6B]" : "border-slate-200 text-slate-600"}`}>{d}</button>))}</div></div>
              <div><label className="text-sm font-medium text-slate-700">Description du projet</label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1.5" placeholder="Décrivez votre projet…" /></div>
              <div className="flex justify-between"><button onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 text-slate-600 font-semibold"><ArrowLeft className="w-4 h-4" /> Retour</button><button data-testid="devis-next-2" onClick={() => setStep(3)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0F2A6B] text-white font-semibold">Suivant <ArrowRight className="w-4 h-4" /></button></div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900 font-head">Vos coordonnées</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm font-medium text-slate-700">Nom complet *</label><Input data-testid="devis-nom" value={form.nom} onChange={(e) => set("nom", e.target.value)} className="mt-1.5" /></div>
                <div><label className="text-sm font-medium text-slate-700">Entreprise</label><Input value={form.entreprise} onChange={(e) => set("entreprise", e.target.value)} className="mt-1.5" /></div>
                <div><label className="text-sm font-medium text-slate-700">Email *</label><Input data-testid="devis-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
                <div><label className="text-sm font-medium text-slate-700">Téléphone / WhatsApp</label><Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className="mt-1.5" /></div>
              </div>
              <div className="flex justify-between"><button onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 text-slate-600 font-semibold"><ArrowLeft className="w-4 h-4" /> Retour</button><button data-testid="devis-submit" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#4CB944] text-white font-semibold hover:bg-[#3AA033] disabled:opacity-60">{loading ? "Envoi…" : "Envoyer ma demande"}</button></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
